import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import './ViewComparisonPage.scss';

interface ComparisonPageViewProps {}

interface LocationState { 
    compId?: string;
    comparisonType?: string;
    url1?: string;
    url2?: string;
    customInstruction?: string;
    comparisonData: any;
    fromOtherComparison?: boolean;
    fromExisting?: boolean;
    fromCustomUrls?: boolean;
    userModifications?: string[];
    fromAdmin: boolean;
    navigationTimestamp: number;
}

const ComparisonPageView: React.FC<ComparisonPageViewProps> = () => {
    const { compId } = useParams<{ compId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [comparisonData, setComparisonData] = useState<any>(null);
    const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
    const [switchingVersion, setSwitchingVersion] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const serverUrl = process.env.REACT_APP_DRF_DOMAIN || 'http://localhost:8000';

    const state = location.state as LocationState;

    const [copiedButton, setCopiedButton] = useState<string | null>(null);

    
    const copyToClipboard = async (text: string, buttonId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedButton(buttonId);
            
            setTimeout(() => {
                setCopiedButton(null);
            }, 2000);
            
        } catch (err) {
            console.error('Failed to copy text: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            setCopiedButton(buttonId);
            setTimeout(() => {
                setCopiedButton(null);
            }, 2000);
        }
    };

    useEffect(() => {
        if (state && state.comparisonData) {
            setComparisonData(state.comparisonData);
            setCurrentVersionId(state.comparisonData.current_version_id || null);
            setLoading(false);
        } else if (compId) {
            fetchCompPageData(compId);
        } else {
            setError('No Comp ID provided');
            setLoading(false);
        }
    }, [compId, state]);

    const fetchCompPageData = async (id: string) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_DRF_DOMAIN}/api/admin/get_ai_comparison_page_data/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ comp_id: id })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.status === 'success') {
                setComparisonData(data.comparison_data);
                setCurrentVersionId(data.current_version_id || null);
            } else {
                throw new Error(data.message || 'Failed to fetch Comp data');
            }
        } catch (err) {
            console.error('Error fetching comp data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch comp data');
        } finally {
            setLoading(false);
        }
    };

    const switchToVersionAdmin = async (versionId: number) => {
        if (!comparisonData) {
            console.error('Comparison data not available');
            return;
        }

        const selectedVersion = comparisonData.versions?.find((v: any) => v.id === versionId);
        if (!selectedVersion) {
            console.error('Selected version not found');
            return;
        }

        try {
            setSwitchingVersion(true);

            const updatedComparisonData = {
                ...comparisonData,
                versions: comparisonData.versions.map((version: any) => ({
                    ...version,
                    isDisplayed: version.id === versionId
                }))
            };

            setComparisonData(updatedComparisonData);
            setCurrentVersionId(versionId);

        } catch (error) {
            console.error('Failed to switch version:', error);
            setError('Failed to switch version');
        } finally {
            setSwitchingVersion(false);
        }
    };

    // Get the HTML content to display (either current or selected version)
    const getDisplayHTML = () => {
        if (!comparisonData?.versions?.length) return '';
        
        const displayedVersion = comparisonData.versions.find((v: any) => v.isDisplayed) || 
                                comparisonData.versions.find((v: any) => v.id === currentVersionId) ||
                                comparisonData.versions[0];
        
        return displayedVersion?.html_code || '';
    };

    useEffect(() => {
        if (iframeRef.current && comparisonData) {
            const htmlContent = getDisplayHTML();
            if (htmlContent) {
                // Create a complete HTML document for the iframe
                const fullHtmlContent = `
                    <!DOCTYPE html>
                    <html lang="en">
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <title>Comparison Content</title>
                        <style>
                            /* Reset styles to prevent inheritance issues */
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }
                            
                            body {
                                margin: 0;
                                padding: 20px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'sans-serif';
                                line-height: 1.6;
                                color: #333;
                                background: #fff;
                            }
                        </style>
                    </head>
                    <body>
                        ${htmlContent}
                    </body>
                    </html>
                `;

                // Write content to iframe
                const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
                if (iframeDoc) {
                    iframeDoc.open();
                    iframeDoc.write(fullHtmlContent);
                    iframeDoc.close();

                    // Auto-resize iframe based on content
                    const resizeIframe = () => {
                        try {
                            const iframeBody = iframeDoc.body;
                            const iframeHtml = iframeDoc.documentElement;
                            
                            if (iframeBody && iframeHtml && iframeRef.current) {
                                const height = Math.max(
                                    iframeBody.scrollHeight,
                                    iframeBody.offsetHeight,
                                    iframeHtml.clientHeight,
                                    iframeHtml.scrollHeight,
                                    iframeHtml.offsetHeight
                                );
                                
                                // Set minimum height and add some padding
                                const finalHeight = Math.max(height + 40, 400);
                                iframeRef.current.style.height = `${finalHeight}px`;
                            }
                        } catch (error) {
                            console.error('Error resizing iframe:', error);
                            // Fallback height
                            if (iframeRef.current) {
                                iframeRef.current.style.height = '600px';
                            }
                        }
                    };

                    // Resize after content loads
                    setTimeout(resizeIframe, 100);
                    
                    // Also resize on window resize
                    const handleResize = () => setTimeout(resizeIframe, 100);
                    window.addEventListener('resize', handleResize);
                    
                    // Cleanup
                    return () => window.removeEventListener('resize', handleResize);
                }
            }
        }
    }, [comparisonData, currentVersionId]);

    if (loading) {
        return (
            <div className="comp-page-container">
                <div className="comp-page-loading">
                    <div className="comp-loading-spinner">Loading comp page...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="comp-page-container">
                <div className="comp-error-notification">
                    <button className="comp-error-close" onClick={() => navigate(-1)}>×</button>
                    <strong>Error:</strong> {error}
                </div>
                <button className="comp-back-button" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    if (!comparisonData) {
        return (
            <div className="comp-page-container">
                <div className="comp-warning-notification">
                    No Comparison data available
                </div>
                <button className="comp-back-button" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="comp-page-container">
            {/* Admin Header */}
            <div className="comp-admin-card">
                <div className="comp-admin-header">
                    <div className="comp-admin-header-left">
                        <svg 
                            className="back-btn"
                            width="30"
                            height="24"
                            viewBox="0 0 30 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => navigate(-1)}
                        >
                            <path d="M26.0435 12.0003H2.82031M2.82031 12.0003L12.8382 1.98242M2.82031 12.0003L12.8382 22.0181" stroke="black" strokeOpacity="0.5" strokeWidth="3" />
                        </svg>
                        <p className="comp-admin-title">
                            Admin View - comp Page
                        </p>
                    </div>
                </div>
                <div className="comp-admin-content">
                    <div className="comp-admin-row">
                        <div className="comp-admin-col">
                            <strong>Comparison Type:</strong> {comparisonData.comparison_type}
                        </div>
                    </div>
                    <div className="comp-admin-row">
                        <div className="comp-admin-col">
                            <strong>Comparison ID:</strong> {comparisonData.comp_id}
                        </div>
                    </div>
                    {comparisonData.admin_info && (
                        <div className="comp-admin-row">
                            <div className="comp-admin-col">
                                <strong>User:</strong> {comparisonData.admin_info.user_email}
                            </div>
                            <div className="comp-admin-col">
                                <strong>Website:</strong> {comparisonData.admin_info.website_domain}
                            </div>
                            <div className="comp-admin-col">
                                <strong>Plan:</strong> {comparisonData.admin_info.user_plan}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            
            <div className="comp-content-card">
                <div className="comp-content-header">
                    <p className="comp-content-title">
                        Comparison Page Content
                        {currentVersionId && (
                            <span className="comp-version-indicator">
                                (Viewing Version: {comparisonData.versions?.find((v: any) => v.id === currentVersionId)?.version_name || 'Unknown'})
                            </span>
                        )}
                    </p>
                </div>
                <div className="comp-content-body">
                    {comparisonData.versions && comparisonData.versions.length > 0 ? (
                        <div className="comp-content-isolation">
                            <iframe
                                ref={iframeRef}
                                style={{
                                    width: '100%',
                                    minHeight: '400px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#fff'
                                }}
                                sandbox="allow-scripts allow-same-origin allow-forms"
                                title="Comparison Content"
                            />
                        </div>
                    ) : (
                        <div className="comp-no-content">
                            No content available for this comp page
                        </div>
                    )}
                </div>
            </div>

            {/* Enhanced Version History with Switching */}
            {comparisonData.versions && comparisonData.versions.length > 1 && (
                <div className="comp-version-card">
                    <div className="comp-version-header">
                        <p className="comp-version-title">
                            Version History ({comparisonData.versions.length} versions)
                        </p>
                        <p className="comp-version-subtitle">
                            Click on a version to preview it (admin preview only)
                        </p>
                    </div>
                    <div className="comp-version-content">
                        {comparisonData.versions.map((version: any, index: number) => {
                            const isCurrentlyDisplayed = version.id === currentVersionId || 
                                                       (currentVersionId === null && index === 0);
                            
                            return (
                                <div 
                                    key={version.id} 
                                    className={`comp-version-item ${isCurrentlyDisplayed ? 'active' : ''} ${switchingVersion ? 'disabled' : ''}`}
                                    onClick={() => !switchingVersion && switchToVersionAdmin(version.id)}
                                    style={{ cursor: switchingVersion ? 'not-allowed' : 'pointer' }}
                                >
                                    <div className="comp-version-row">
                                        <div className="comp-version-col">
                                            <strong>Version:</strong> {version.version_name}
                                        </div>
                                        <div className="comp-version-col">
                                            <strong>Created:</strong> {new Date(version.created_on).toLocaleString()}
                                        </div>
                                        <div className="comp-version-col">
                                            <div className="comp-version-badges">
                                                {switchingVersion && <span className="comp-loading-tag">Loading...</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="comp-version-actions">
                                        <button 
                                            className="comp-version-preview-btn"
                                            disabled={switchingVersion || isCurrentlyDisplayed}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                switchToVersionAdmin(version.id);
                                            }}
                                        >
                                            {isCurrentlyDisplayed ? 'Currently Viewing' : 'Preview This Version'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="comp-version-card" style={{marginTop: '20px'}}>
                <div className="comp-version-header">
                    <p className="comp-version-title">
                        Embed Code
                    </p>
                </div>
                <div className="comp-version-content">
                    <div className={`comp-version-item`}>
                        <div className="comp-version-row">
                            <div className="comp-version-col">
                                <strong>Script tag: </strong> 
                                <h3>
                                    {`<script src="${serverUrl}/api/frontend/get-universal-comparison-script/?plan=Trial" defer></script>`}
                                </h3>
                            </div>
                        </div>
                        <div className="comp-version-actions">
                            <button 
                                className='comp-version-preview-btn'
                                onClick={() => copyToClipboard(`<script src="${serverUrl}/api/frontend/get-universal-comparison-script/?plan=Trial" defer></script>`, 'script-copy')}
                                style={{
                                    transform: copiedButton === 'script-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'script-copy' ? '#007BFF' : '',
                                    transition: 'all 0.1s ease-in-out',
                                }}
                            >
                                {copiedButton === 'script-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                    
                    <div className={`comp-version-item`}>
                        <div className="comp-version-row">
                            <div className="comp-version-col">
                                <strong>Div tag: </strong>
                                <h3>
                                    {`<div data-comp-id="${comparisonData.comp_id || 'comp_id'}" style="width: 100%; min-height: 400px; display: block; position: relative; margin: 0; padding: 0; box-sizing: border-box; background: transparent; border: none; outline: none;"></div>`}
                                </h3> 
                            </div>
                        </div>
                        <div className="comp-version-actions">
                            <button 
                                className='comp-version-preview-btn'
                                onClick={() => copyToClipboard(`<div data-comp-id="${comparisonData.comp_id || 'comp_id'}" style="width: 100%; min-height: 400px; display: block; position: relative; margin: 0; padding: 0; box-sizing: border-box; background: transparent; border: none; outline: none;"></div>`, 'div-copy')}
                                style={{
                                    transform: copiedButton === 'div-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'div-copy' ? '#007BFF' : '',
                                    color: copiedButton === 'div-copy' ? 'white' : '',
                                    transition: 'all 0.2s ease-in-out',
                                    boxShadow: copiedButton === 'div-copy' ? '0 4px 8px rgba(76, 175, 80, 0.3)' : ''
                                }}
                            >
                                {copiedButton === 'div-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Extend Window interface to include Chart
declare global {
    interface Window {
        Chart: any;
    }
}

export default ComparisonPageView;